import React, { useState, useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Card, Btn, InfoBanner } from '../../common/UI';
import API from '../../../utils/api';

// Internal, login-required label printing page. Prints one label per package
// (1 of N, 2 of N...) with logo, vessel, PO, supplier, entry date, per-item
// weight/dimensions, and location, plus a QR code linking to the public
// no-login soft-copy page. Stock ID, Transit ID, IMO number are excluded
// per client requirement.
export default function PrintLabels({ onNav, stockEntry }) {
  const [labelData, setLabelData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    // No entry selected (e.g. direct refresh) — bounce back to Stock List.
    if (!stockEntry?.id) {
      onNav('stock-list');
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        // Generates label_token on first call, returns the existing one after.
        const res = await API.get(`/warehouse/stock/${stockEntry.id}/labels`);
        if (!cancelled) setLabelData(res.data);
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.message || 'Failed to load labels');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [stockEntry, onNav]);

  if (loading) {
    return <div style={{ padding: 24 }}><Card><p style={{ padding: 20 }}>Label is loading...</p></Card></div>;
  }

  if (error || !labelData) {
    return (
      <div style={{ padding: 24 }}>
        <Card>
          <div style={{ padding: 20 }}>
            <InfoBanner variant="danger">⚠ {error || 'Could not load label data'}</InfoBanner>
            <Btn variant="outline" onClick={() => onNav('stock-list')} style={{ marginTop: 12 }}>← Back to Stock List</Btn>
          </div>
        </Card>
      </div>
    );
  }

  // "dimensions" and "item_weights" are comma-separated, same order — matched by index.
  const dimensionsList = (labelData.dimensions || '').split(',').map(d => d.trim()).filter(Boolean);
  const weightsList = (labelData.item_weights || '').split(',').map(w => w.trim()).filter(Boolean);
  const totalLabels = Number(labelData.quantity) || dimensionsList.length || 1;

  // PUBLIC_URL keeps this correct on both staging (/stage subpath) and production.
  const publicBase = `${window.location.origin}${process.env.PUBLIC_URL || ''}`;
  const qrUrl = `${publicBase}/label/${labelData.label_token}`;

  const entryDateDisplay = labelData.entry_date
    ? new Date(labelData.entry_date).toLocaleDateString('en-GB')
    : '—';

  const labels = Array.from({ length: totalLabels }, (_, i) => ({
    index: i + 1,
    dimension: dimensionsList[i] || dimensionsList[0] || '—',
    weight: weightsList[i] || '—'
  }));

  return (
    <div style={{ padding: 24 }}>
      {/* Print shows only .label-print-area; rest of the app (sidebar/topbar) is hidden. */}
      <style>{`
        @media print {
          body * { visibility: hidden; }
          .label-print-area, .label-print-area * { visibility: visible; }
          .label-print-area { position: absolute; left: 0; top: 0; width: 100%; margin: 0; padding: 0; }
          .no-print { display: none !important; }
          @page { size: 4in 6in; margin: 0.15in; }
          .label-card { page-break-after: always; box-shadow: none !important; border: 1px solid #000 !important; }
        }
      `}</style>

      <div className="no-print" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <h2 style={{ margin: 0 }}>🏷 Print Labels — {labelData.po_numbers}</h2>
          <p style={{ margin: '4px 0 0', color: 'var(--muted)', fontSize: 13 }}>{totalLabels} label(s) will print — one per package</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Btn variant="outline" onClick={() => onNav('stock-list')}>← Back</Btn>
          <Btn variant="primary" onClick={() => window.print()}>🖨 Print</Btn>
        </div>
      </div>

      <div className="label-print-area" style={{ display: 'flex', flexDirection: 'column', gap: 16, alignItems: 'center' }}>
        {labels.map(item => (
          <div key={item.index} className="label-card" style={{
            width: '4in', minHeight: '6in', boxSizing: 'border-box', border: '1px solid #d1d5db',
            borderRadius: 8, padding: 16, background: '#fff', color: '#111',
            display: 'flex', flexDirection: 'column', boxShadow: '0 1px 4px rgba(0,0,0,0.08)'
          }}>
            {/* Header: Company logo + name */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, borderBottom: '2px solid #111', paddingBottom: 8, marginBottom: 10 }}>
              <img src="/logo.png" alt="All In Logistics" style={{ width: 40, height: 40, objectFit: 'contain' }} />
              <div>
                <div style={{ fontWeight: 800, fontSize: 15, letterSpacing: 0.5 }}>ALL IN LOGISTICS</div>
                <div style={{ fontSize: 10, color: '#555' }}>WAREHOUSE SYSTEM</div>
              </div>
            </div>

            {/* Core cargo fields per client requirement */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13, flex: 1 }}>
              <Field label="Vessel" value={labelData.vessel || '—'} />
              <Field label="PO Number" value={labelData.po_numbers} mono />
              <Field label="Supplier" value={labelData.supplier || '—'} />
              <Field label="Entry Date" value={entryDateDisplay} />
              <Field label="Package" value={`${item.index} of ${totalLabels}`} strong />
              <Field label="Weight" value={`${item.weight} ${labelData.packaging_unit || 'KGS'}`} />
              <Field label="Dimensions" value={item.dimension.replace(/x/gi, ' x ')} />
              <Field label="Warehouse Location" value={labelData.location || '—'} />
            </div>

            {/* QR code — opens the public soft-copy page, no login */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: 12, paddingTop: 10, borderTop: '1px dashed #999' }}>
              <QRCodeSVG value={qrUrl} size={90} level="M" />
              <div style={{ fontSize: 9, color: '#666', marginTop: 4 }}>Scan for soft copy</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Renders one "Label: Value" row on the label
function Field({ label, value, mono, strong }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
      <span style={{ color: '#555', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.3 }}>{label}</span>
      <span style={{
        fontWeight: strong ? 800 : 600,
        fontFamily: mono ? "'JetBrains Mono',monospace" : 'inherit',
        textAlign: 'right'
      }}>{value}</span>
    </div>
  );
}
